import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";

/**
 * Shared client-side filters for camp-based report views
 * (Camp Attendance, Registered Student List - Camp).
 *
 * Holds the filter state, loads the dropdown options (same sources as the
 * Camps page) and returns the rows that pass the active filters.
 *
 * `programField` is the row field the Program Type filter compares against:
 * the attendance endpoint returns it as `program_name`, the camp report
 * endpoint as `program_type`.
 */
export function useCampFilters(reports, branch, { programField = "program_name" } = {}) {
  const [batchTypeFilter, setBatchTypeFilter] = useState(""); // batch_type_id
  const [campTypeFilter, setCampTypeFilter] = useState("");
  const [programTypeFilter, setProgramTypeFilter] = useState("");
  const [streamFilter, setStreamFilter] = useState([]); // array of course_code
  const [batchTypeOptions, setBatchTypeOptions] = useState([]); // for react-select
  const [programTypes, setProgramTypes] = useState([]);
  const [streamOptions, setStreamOptions] = useState([]); // grouped for react-select

  // Camp Type options come from the distinct values present in the results.
  const campTypeOptions = useMemo(
    () =>
      [...new Set((reports || []).map((r) => r.camp_type).filter(Boolean))].sort(),
    [reports]
  );

  // Program Type + Stream Name options mirror the Camps page sources.
  useEffect(() => {
    let cancelled = false;
    const loadOptions = async () => {
      try {
        const [batchRes, progRes, courseRes] = await Promise.all([
          axios("api/camps/batch-type"),
          axios("api/camps/program-type"),
          axios(`api/camps/course/${branch.branch_id}`),
        ]);
        if (cancelled) return;

        // Camp/batch types (exclude id 1, same as the Camps page).
        setBatchTypeOptions(
          (batchRes.data.results || [])
            .filter((bt) => bt.id !== 1)
            .map((bt) => ({ value: bt.id, label: bt.value }))
        );

        const programs = progRes.data.results || [];
        const courses = courseRes.data.results || [];
        setProgramTypes(programs);

        const grouped = programs.map((pt) => ({
          label: pt.value,
          options: courses
            .filter((c) => String(c.program_type) === String(pt.id))
            .sort((a, b) => (a.value > b.value ? 1 : -1))
            .map((a) => ({ value: a.value, label: `${a.value} - ${a.abbr}` })),
        }));
        setStreamOptions(grouped);
      } catch (e) {
        console.log("Camp filter options error: ", e);
      }
    };
    if (branch && branch.branch_id) loadOptions();
    return () => {
      cancelled = true;
    };
  }, [branch]);

  const filteredReports = useMemo(() => {
    return (reports || []).filter((r) => {
      if (batchTypeFilter && String(r.camp_type_id) !== String(batchTypeFilter))
        return false;
      if (campTypeFilter && r.camp_type !== campTypeFilter) return false;
      if (programTypeFilter && r[programField] !== programTypeFilter)
        return false;
      if (streamFilter.length > 0 && !streamFilter.includes(r.course_code))
        return false;
      return true;
    });
  }, [reports, batchTypeFilter, campTypeFilter, programTypeFilter, streamFilter, programField]);

  return {
    filteredReports,
    filters: {
      batchTypeFilter,
      setBatchTypeFilter,
      campTypeFilter,
      setCampTypeFilter,
      programTypeFilter,
      setProgramTypeFilter,
      setStreamFilter,
      batchTypeOptions,
      campTypeOptions,
      programTypes,
      streamOptions,
    },
  };
}

export default function CampFilters({ filters }) {
  const {
    batchTypeFilter,
    setBatchTypeFilter,
    campTypeFilter,
    setCampTypeFilter,
    programTypeFilter,
    setProgramTypeFilter,
    setStreamFilter,
    batchTypeOptions,
    campTypeOptions,
    programTypes,
    streamOptions,
  } = filters;

  return (
    <div className="form-group row align-items-end">
      <div className="col-lg-3">
        <Select
          isClearable
          placeholder="Select Camp Type"
          options={batchTypeOptions}
          value={
            batchTypeOptions.find((o) => o.value === batchTypeFilter) || null
          }
          onChange={(selected) =>
            setBatchTypeFilter(selected ? selected.value : "")
          }
        />
        <small className="form-text text-muted">
          <b>Select</b> Camp Type
        </small>
      </div>

      <div className="col-lg-3">
        <select
          className="form-control"
          value={campTypeFilter}
          onChange={(e) => setCampTypeFilter(e.target.value)}
        >
          <option value="">All</option>
          {campTypeOptions.map((ct) => (
            <option key={ct} value={ct}>
              {ct}
            </option>
          ))}
        </select>
        <small className="form-text text-muted">
          <b>Filter</b> by Camp Type
        </small>
      </div>

      <div className="col-lg-3">
        <select
          className="form-control"
          value={programTypeFilter}
          onChange={(e) => setProgramTypeFilter(e.target.value)}
        >
          <option value="">All</option>
          {programTypes.map((pt) => (
            <option key={pt.id} value={pt.value}>
              {pt.value}
            </option>
          ))}
        </select>
        <small className="form-text text-muted">
          <b>Filter</b> by Program Type
        </small>
      </div>

      <div className="col-lg-3">
        <Select
          isMulti
          placeholder="Select..."
          options={streamOptions}
          onChange={(selected) =>
            setStreamFilter((selected || []).map((c) => c.value))
          }
        />
        <small className="form-text text-muted">
          <b>Filter</b> by Stream Name
        </small>
      </div>
    </div>
  );
}
